import { supabase } from '@/integrations/supabase/client';
import { UnifiedUser } from '@/hooks/useUnifiedUser';
import { EnhancedNotification, NotificationType, NotificationCategory, NotificationPriority } from '@/types/enhancedNotifications';
import { Notification } from '@/types/notifications';

interface NotificationData {
  user_id: string;
  user_type: 'supabase' | 'internal';
  notification_type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  priority?: NotificationPriority;
  requires_action?: boolean;
  action_url?: string;
  expires_at?: string;
}

interface GameNotificationData {
  gameId: string;
  gameTitle: string;
  gameDate: string;
  gameTime: string;
  venueId?: string;
  teamIds: string[];
  type: 'reminder' | 'confirmation' | 'roster_deadline' | 'status_change';
  reminderTime?: '24h' | '2h' | '30min';
}

interface MentionNotificationData {
  from_user_id: string;
  from_username: string;
  to_user_id: string;
  chat_group_id: string;
  message_id: string;
  message_preview: string;
  chat_group_name: string;
}

export const comprehensiveNotificationService = {
  // ==================== CORE NOTIFICATION MANAGEMENT ====================
  
  async createNotification(data: NotificationData): Promise<EnhancedNotification | null> {
    try {
      console.log('=== CREATING COMPREHENSIVE NOTIFICATION ===');
      console.log('Type:', data.notification_type, 'Category:', data.category, 'User:', data.user_id, 'UserType:', data.user_type);

      const insertData = {
        user_id: data.user_id,
        user_type: data.user_type,
        notification_type: data.notification_type,
        category: data.category,
        title: data.title,
        message: data.message,
        metadata: data.metadata || {},
        priority: data.priority || 'normal',
        requires_action: data.requires_action || false,
        action_url: data.action_url,
        expires_at: data.expires_at
      };

      console.log('📝 Inserting notification data:', insertData);

      const { data: notification, error } = await supabase
        .from('notifications')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating notification:', error);
        console.error('❌ Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return null;
      }

      const typedNotification = {
        ...notification,
        user_type: notification.user_type as 'supabase' | 'internal'
      } as EnhancedNotification;
      
      this.broadcastNotification(typedNotification);
      return typedNotification;
    } catch (error) {
      console.error('❌ Error in createNotification:', error);
      return null;
    }
  },

  async getNotifications(user: UnifiedUser | null): Promise<EnhancedNotification[]> {
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('user_type', user.userType)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching notifications:', error);
        return [];
      }

      return (data || []).map(notification => ({
        ...notification,
        user_type: notification.user_type as 'supabase' | 'internal'
      })) as EnhancedNotification[];
    } catch (error) {
      console.error('❌ Error in getNotifications:', error);
      return [];
    }
  },

  async getUnreadCount(user: UnifiedUser | null): Promise<number> {
    if (!user) return 0;

    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('user_type', user.userType)
        .eq('is_read', false);

      if (error) {
        console.error('❌ Error fetching unread count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('❌ Error in getUnreadCount:', error);
      return 0;
    }
  },

  async markAsRead(notificationId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) {
        console.error('❌ Error marking notification as read:', error);
        return false;
      }

      this.broadcastUpdate();
      return true;
    } catch (error) {
      console.error('❌ Error in markAsRead:', error);
      return false;
    }
  },

  async markAllAsRead(user: UnifiedUser | null): Promise<boolean> {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('user_type', user.userType)
        .eq('is_read', false);

      if (error) {
        console.error('❌ Error marking all as read:', error);
        return false;
      }

      this.broadcastUpdate();
      return true;
    } catch (error) {
      console.error('❌ Error in markAllAsRead:', error);
      return false;
    }
  },

  // ==================== GAME NOTIFICATIONS ====================
  
  async createGameNotification(data: GameNotificationData): Promise<void> {
    try {
      console.log('🎮 Creating game notifications for game:', data.gameId);

      // Convert teamIds to numbers for database queries
      const teamIdNumbers = data.teamIds.map(id => parseInt(id)).filter(id => !isNaN(id));
      
      // Get all relevant users for this game
      const relevantUsers = await this.getGameParticipants(data.gameId, teamIdNumbers, data.venueId);
      
      for (const user of relevantUsers) {
        let title = '';
        let message = '';
        let priority: NotificationPriority = 'normal';
        let requiresAction = false;
        
        switch (data.type) {
          case 'reminder':
            title = `Game Reminder: ${data.gameTitle}`;
            message = `Your game "${data.gameTitle}" is scheduled for ${data.gameDate} at ${data.gameTime}`;
            if (data.reminderTime === '30min') {
              priority = 'high';
              message += ' - Starting in 30 minutes!';
            }
            break;
            
          case 'confirmation':
            title = `Game Confirmed: ${data.gameTitle}`;
            message = `Your game "${data.gameTitle}" has been confirmed for ${data.gameDate} at ${data.gameTime}`;
            requiresAction = true;
            break;
            
          case 'roster_deadline':
            title = `Roster Deadline Warning`;
            message = `Roster deadline approaching for "${data.gameTitle}" on ${data.gameDate}`;
            priority = 'urgent';
            requiresAction = true;
            break;
            
          case 'status_change':
            title = `Game Status Update`;
            message = `Status updated for "${data.gameTitle}" on ${data.gameDate}`;
            break;
        }

        await this.createNotification({
          user_id: user.id,
          user_type: user.type,
          notification_type: 'game_notification',
          category: 'game',
          title,
          message,
          metadata: {
            gameId: data.gameId,
            gameTitle: data.gameTitle,
            gameDate: data.gameDate,
            gameTime: data.gameTime,
            reminderType: data.reminderTime
          },
          priority,
          requires_action: requiresAction,
          action_url: `/games/${data.gameId}`
        });
      }
    } catch (error) {
      console.error('❌ Error creating game notifications:', error);
    }
  },

  // ==================== POINTS NOTIFICATIONS ====================
  
  async createPointsNotification(userId: string, userType: 'supabase' | 'internal', pointsData: {
    points: number;
    transactionType: string;
    description?: string;
    newLevel?: string;
  }): Promise<void> {
    try {
      let title = '';
      let message = '';
      let priority: NotificationPriority = 'normal';
      
      if (pointsData.transactionType === 'level_up' && pointsData.newLevel) {
        title = `Level Up! You're now ${pointsData.newLevel}`;
        message = `Congratulations! You've reached ${pointsData.newLevel} level`;
        priority = 'high';
      } else if (pointsData.points > 0) {
        title = `+${pointsData.points} Points Earned!`;
        message = pointsData.description || `You earned ${pointsData.points} points`;
      } else {
        title = `${Math.abs(pointsData.points)} Points Spent`;
        message = pointsData.description || `You spent ${Math.abs(pointsData.points)} points`;
      }

      await this.createNotification({
        user_id: userId,
        user_type: userType,
        notification_type: 'achievement',
        category: 'general',
        title,
        message,
        metadata: pointsData,
        priority,
        action_url: '/profile'
      });
    } catch (error) {
      console.error('❌ Error creating points notification:', error);
    }
  },

  // ==================== SCORE NOTIFICATIONS ====================
  
  async createScoreNotification(data: {
    playerId: string;
    playerType: 'supabase' | 'internal';
    scorekeeperId: string;
    gameId: string;
    statType: string;
    statValue: number;
    requiresApproval: boolean;
  }): Promise<void> {
    try {
      if (data.requiresApproval) {
        // Notify player about score submission requiring approval
        await this.createNotification({
          user_id: data.playerId,
          user_type: data.playerType,
          notification_type: 'performance_update',
          category: 'game',
          title: 'Score Submitted for Approval',
          message: `Your ${data.statType} score of ${data.statValue} has been submitted and requires approval`,
          metadata: data,
          priority: 'normal',
          requires_action: false,
          action_url: '/score-approvals'
        });

        // Notify coaches/team owners about approval needed
        const teamApprovers = await this.getTeamApprovers(data.playerId, data.playerType);
        
        for (const approver of teamApprovers) {
          await this.createNotification({
            user_id: approver.id,
            user_type: approver.type,
            notification_type: 'needs_attention',
            category: 'coach',
            title: 'Score Approval Required',
            message: `Player score requires your approval: ${data.statType} - ${data.statValue}`,
            metadata: data,
            priority: 'high',
            requires_action: true,
            action_url: '/score-approvals'
          });
        }
      } else {
        // Direct score confirmation
        await this.createNotification({
          user_id: data.playerId,
          user_type: data.playerType,
          notification_type: 'performance_update',
          category: 'game',
          title: 'Score Recorded',
          message: `Your ${data.statType} score of ${data.statValue} has been recorded`,
          metadata: data,
          priority: 'normal'
        });
      }
    } catch (error) {
      console.error('❌ Error creating score notification:', error);
    }
  },

  // ==================== MENTIONS NOTIFICATIONS ====================
  
  async createMentionNotification(data: MentionNotificationData): Promise<void> {
    try {
      console.log('@ Creating mention notification:', {
        to_user: data.to_user_id,
        from_user: data.from_username,
        group: data.chat_group_name,
        message_preview: data.message_preview.substring(0, 50) + '...'
      });

      // Determine user type
      const userType = await this.getUserType(data.to_user_id);
      if (!userType) {
        console.error('❌ Could not determine user type for mention notification:', data.to_user_id);
        return;
      }

      console.log(`@ User type determined: ${data.to_user_id} -> ${userType}`);

      const notification = await this.createNotification({
        user_id: data.to_user_id,
        user_type: userType,
        notification_type: 'mention',
        category: 'chat',
        title: `@mention from ${data.from_username}`,
        message: data.message_preview,
        metadata: {
          from_user_id: data.from_user_id,
          from_username: data.from_username,
          chat_group_id: data.chat_group_id,
          message_id: data.message_id,
          chat_group_name: data.chat_group_name
        },
        priority: 'normal',
        action_url: `/chat/${data.chat_group_id}`
      });

      if (notification) {
        console.log('✅ Mention notification created successfully:', notification.id);
      } else {
        console.error('❌ Failed to create mention notification - null returned');
      }
    } catch (error) {
      console.error('❌ Error creating mention notification:', error);
    }
  },

  // ==================== DEALS & PROMOTIONS ====================
  
  async createDealNotification(userId: string, userType: 'supabase' | 'internal', dealData: {
    dealId: string;
    title: string;
    company: string;
    discount: string;
    category: string;
    expiresAt: string;
  }): Promise<void> {
    try {
      await this.createNotification({
        user_id: userId,
        user_type: userType,
        notification_type: 'feature_announcement',
        category: 'general',
        title: `New Deal: ${dealData.title}`,
        message: `${dealData.company} is offering ${dealData.discount} in ${dealData.category}`,
        metadata: dealData,
        priority: 'low',
        action_url: `/deals/${dealData.dealId}`,
        expires_at: dealData.expiresAt
      });
    } catch (error) {
      console.error('❌ Error creating deal notification:', error);
    }
  },

  // ==================== SUPPORT TICKET NOTIFICATIONS ====================
  
  async createTicketNotification(data: {
    userId: string;
    userType: 'supabase' | 'internal';
    ticketId: string;
    ticketNumber: string;
    status: string;
    type: 'created' | 'updated' | 'responded';
    adminResponse?: string;
  }): Promise<void> {
    try {
      let title = '';
      let message = '';
      let priority: NotificationPriority = 'normal';
      
      switch (data.type) {
        case 'created':
          title = `Support Ticket Created`;
          message = `Your support ticket #${data.ticketNumber} has been created and is being reviewed`;
          break;
          
        case 'updated':
          title = `Ticket Status Updated`;
          message = `Your support ticket #${data.ticketNumber} status has been updated to: ${data.status}`;
          break;
          
        case 'responded':
          title = `New Response on Your Ticket`;
          message = `Support team responded to ticket #${data.ticketNumber}`;
          priority = 'high';
          break;
      }

      await this.createNotification({
        user_id: data.userId,
        user_type: data.userType,
        notification_type: 'system_update',
        category: 'system',
        title,
        message,
        metadata: {
          ticketId: data.ticketId,
          ticketNumber: data.ticketNumber,
          status: data.status,
          adminResponse: data.adminResponse
        },
        priority,
        action_url: `/support/ticket/${data.ticketId}`
      });
    } catch (error) {
      console.error('❌ Error creating ticket notification:', error);
    }
  },

  // ==================== HELPER METHODS ====================
  
  async getGameParticipants(gameId: string, teamIds: number[], venueId?: string): Promise<Array<{id: string, type: 'supabase' | 'internal'}>> {
    const participants: Array<{id: string, type: 'supabase' | 'internal'}> = [];

    try {
      // Get team members
      if (teamIds.length > 0) {
        const { data: teamPlayers } = await supabase
          .from('team_players')
          .select('user_id')
          .in('team_id', teamIds);

        if (teamPlayers) {
          teamPlayers.forEach(player => {
            participants.push({ id: player.user_id, type: 'supabase' });
          });
        }

        // Get team coaches
        const { data: teamCoaches } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .in('team_id', teamIds);

        if (teamCoaches) {
          teamCoaches.forEach(coach => {
            participants.push({ id: coach.coach_id, type: 'supabase' });
          });
        }
      }

      // Add venue owner if specified
      if (venueId) {
        participants.push({ id: venueId, type: 'supabase' });
      }

      return participants;
    } catch (error) {
      console.error('❌ Error getting game participants:', error);
      return [];
    }
  },

  async getTeamApprovers(playerId: string, playerType: 'supabase' | 'internal'): Promise<Array<{id: string, type: 'supabase' | 'internal'}>> {
    const approvers: Array<{id: string, type: 'supabase' | 'internal'}> = [];

    try {
      // Get player's teams
      const { data: playerTeams } = await supabase
        .from('team_players')
        .select('team_id')
        .eq('user_id', playerId);

      if (playerTeams) {
        const teamIds = playerTeams.map(t => t.team_id);

        // Get team coaches
        const { data: coaches } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .in('team_id', teamIds);

        if (coaches) {
          coaches.forEach(coach => {
            approvers.push({ id: coach.coach_id, type: 'supabase' });
          });
        }

        // Get team owners
        const { data: teams } = await supabase
          .from('teams')
          .select('created_by')
          .in('team_id', teamIds);

        if (teams) {
          teams.forEach(team => {
            approvers.push({ id: team.created_by, type: 'supabase' });
          });
        }
      }

      return approvers;
    } catch (error) {
      console.error('❌ Error getting team approvers:', error);
      return [];
    }
  },

  async getUserType(userId: string): Promise<'supabase' | 'internal' | null> {
    try {
      // Check if user exists in profiles (Supabase users)
      const { data: supabaseUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .single();

      if (supabaseUser) return 'supabase';

      // Check if user exists in internal_users
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('id')
        .eq('id', userId)
        .single();

      if (internalUser) return 'internal';

      return null;
    } catch (error) {
      console.error('❌ Error determining user type:', error);
      return 'supabase'; // Default to supabase if we can't determine
    }
  },

  // ==================== BROADCASTING ====================
  
  broadcastNotification(notification: EnhancedNotification): void {
    console.log('📡 Broadcasting comprehensive notification:', notification.notification_type);
    
    const events = [
      new CustomEvent('enhanced-notification', { detail: notification, bubbles: true }),
      new CustomEvent('unified-notification', { detail: notification, bubbles: true }),
      new CustomEvent('notification-update', { detail: { type: 'new', notification }, bubbles: true }),
      new CustomEvent('notification-bell-update', { detail: { notification }, bubbles: true }),
      new CustomEvent('notification-manager-update', { detail: { notification, timestamp: Date.now() }, bubbles: true })
    ];

    events.forEach(event => {
      window.dispatchEvent(event);
      document.dispatchEvent(event);
    });
  },

  broadcastUpdate(): void {
    const events = [
      new CustomEvent('enhanced-notification-update', { detail: { type: 'update' } }),
      new CustomEvent('unified-notification-update', { detail: { type: 'update' } }),
      new CustomEvent('notification-update', { detail: { type: 'update' } }),
      new CustomEvent('notification-bell-update', { detail: { type: 'update' } }),
      new CustomEvent('notification-manager-update', { detail: { timestamp: Date.now() } })
    ];

    events.forEach(event => {
      window.dispatchEvent(event);
      document.dispatchEvent(event);
    });
  }
};